import type { Campaign } from '~/modules/campaign/types';

import {
  DEFAULT_TIME_HIDDEN_ICON_LOADING,
  IThemePagePublishStatus,
  useModalPublishCampaignStore,
} from '~/modules/campaign';
import { usePublishThemePage, useRefetchThemePage } from '~/modules/apps';
import { useUnPublishTemplateB } from './useUnPublishTemplateB';
import { useThemePageStore } from '~/stores/themePage';
import { useShopifyAsset } from '~/modules/shopify';
import { useMemo, useRef } from 'react';
interface UsePublishTemplatesProps {
  callBack: () => void;
  campaigns: Campaign[];
}

export const usePublishTemplates = ({ callBack, campaigns }: UsePublishTemplatesProps) => {
  const closeModalPublishAndStartCampaign = useModalPublishCampaignStore(
    (state) => state.closeModal,
  );
  const setCampaigns = useModalPublishCampaignStore((state) => state.setCampaigns);
  const updateThemePageId = useModalPublishCampaignStore((state) => state.updateThemePageId);
  const publishThemePageSuccess = useModalPublishCampaignStore(
    (state) => state.publishThemePageSuccess,
  );
  const publishThemePageFailed = useModalPublishCampaignStore(
    (state) => state.publishThemePageFailed,
  );
  const themePageIds = useModalPublishCampaignStore((state) => state.themePageIds);

  const { handlePublish } = usePublishThemePage();
  const setThemePagePublished = useThemePageStore((state) => state.setThemePagePublished);
  const { refetch: refetchAssets } = useShopifyAsset();
  const { refetch: refetchThemePage } = useRefetchThemePage();

  const { unPublishedTemplateIdsB } = useUnPublishTemplateB({ campaigns });

  const publishedThemePageIdsRef = useRef<Set<string>>(new Set());

  const isPublishing = useMemo(() => {
    return Object.values(themePageIds).some((themePageId) => themePageId.isLoading);
  }, [themePageIds]);

  const isPublishingByCampaign = useMemo(() => {
    if (unPublishedTemplateIdsB.length === 0) return false;

    return unPublishedTemplateIdsB.some((id) => themePageIds[id]?.isLoading);
  }, [unPublishedTemplateIdsB, themePageIds]);

  const checkAllSuccess = () => {
    return unPublishedTemplateIdsB.every((themePageId) => {
      return publishedThemePageIdsRef.current.has(themePageId);
    });
  };

  const handlePublishSuccess = (themePageId: string) => {
    publishThemePageSuccess(themePageId);
    setThemePagePublished(themePageId);
    publishedThemePageIdsRef.current.add(themePageId);

    setTimeout(() => {
      updateThemePageId(themePageId, {
        status: IThemePagePublishStatus.SUCCESS,
        isLoading: false,
        loadingTime: undefined,
      });
    }, DEFAULT_TIME_HIDDEN_ICON_LOADING);

    if (checkAllSuccess()) {
      // Refetch assets to update the template status(case: unpublished template)
      refetchAssets();
      refetchThemePage();
      callBack();
    }
  };

  const handlePublishError = (themePageId: string) => {
    publishThemePageFailed(themePageId);
  };

  const publishTemplates = () => {
    if (unPublishedTemplateIdsB.length === 0) return;
    setCampaigns(campaigns);
    closeModalPublishAndStartCampaign();

    unPublishedTemplateIdsB.forEach((themePageId) => {
      updateThemePageId(themePageId, { status: IThemePagePublishStatus.DRAFT, isLoading: true });
      handlePublish(
        () => handlePublishSuccess(themePageId),
        () => handlePublishError(themePageId),
        {
          themePageId,
        },
      );
    });
  };

  return {
    publishTemplates,
    unPublishedTemplateIdsB,
    isPublishing,
    isPublishingByCampaign,
  };
};
